import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../app/store';
import SecurityLogin from './SecurityLogin';
import YourPlan from './YourPlan';
import Notifications from './Notifications';
import { ToastContainer, toast } from 'react-toastify';
import PrivacyData from './PrivacyData';
import ApiCredentialsTabs from './ApiCredentialsTabs';
import { getAuthContext } from '../../utils/jwt';
import { setIntegrations, setLoading, setError } from '../../features/integrations/integrationsSlice';
import { BASE_URL } from '../../utils/constants';

// Define the API response type
interface ApiIntegration {
  providerKey: string;
  name: string;
  category: string;
  description: string;
  status: 'CONNECTED' | 'DISCONNECTED';
}

// Logo mapping for provider keys
const logoMap: Record<string, string> = {
  'META_LEAD_ADS': 'https://cdn.worldvectorlogo.com/logos/meta-3.svg',
  'CANVA': 'https://cdn.worldvectorlogo.com/logos/canva-wordmark-2.svg',
  'GOOGLE_LEAD_ADS': 'https://cdn.worldvectorlogo.com/logos/google-ads-2.svg',
};

// Map API category to your category names
const categoryMap: Record<string, string> = {
  'Leads': 'Advertising',
  'Design': 'Design',
};

const Settings: React.FC = () => {
  const dispatch = useDispatch();
  const integrations = useSelector(
    (state: RootState) => state.integrations.items
  );
  const loading = useSelector(
    (state: RootState) => state.integrations.loading
  );

  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const [filterMode, setFilterMode] = useState<'All' | 'Integrated'>('All');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('Integrations');

  const mapStatusToIntegrated = (status: string): boolean => {
    return status === 'CONNECTED';
  };

  const generateIdempotencyKey = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}-${Math.random().toString(36).substring(2, 15)}`;
  };

  const fetchIntegrations = async () => {
    try {
      dispatch(setLoading(true));
      const { token, tenantId, userId } = getAuthContext();

      if (!token || !tenantId) {
        toast.error('Authentication required');
        dispatch(setLoading(false));
        return;
      }

      const idempotencyKey = generateIdempotencyKey();

      const response = await fetch(`${BASE_URL}/api/settings/integrations`, {
        method: 'GET',
        headers: {
          'X-Idempotency-Key': idempotencyKey,
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch integrations: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'OK' && Array.isArray(data.data)) {
        const mappedIntegrations = data.data.map((apiItem: ApiIntegration, index: number) => ({
          id: index + 1,
          name: apiItem.name,
          description: apiItem.description,
          logo: logoMap[apiItem.providerKey] || 'https://via.placeholder.com/48x48?text=Logo',
          integrated: mapStatusToIntegrated(apiItem.status),
          category: categoryMap[apiItem.category] || apiItem.category,
          providerKey: apiItem.providerKey,
          status: apiItem.status,
        }));

        dispatch(setIntegrations(mappedIntegrations));
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error: any) {
      dispatch(setError(error.message || 'Failed to load integrations'));
      toast.error(error.message || 'Failed to load integrations');
      // console.error('Fetch integrations error:', error);
    }
  };

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const categories = Array.from(
    new Set(integrations.map((item) => item.category))
  ).sort();

  const toggleCategory = (cat: string) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const filteredIntegrations = integrations.filter((item) => {
    const matchesSearch = item.name
      .toLowerCase()
      .includes(search.toLowerCase());

    const matchesMode =
      filterMode === 'All' || (filterMode === 'Integrated' && item.integrated);

    const matchesCategory =
      selectedCategories.length === 0 ||
      selectedCategories.includes(item.category);

    return matchesSearch && matchesMode && matchesCategory;
  });

  const handleRefresh = () => {
    fetchIntegrations();
  };

  // Empty state component
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="bg-gray-50 rounded-full p-6 mb-4">
        <svg
          className="w-16 h-16 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
      </div>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">No Integrations Available</h3>
      <p className="text-gray-500 text-center max-w-sm">
        There are no integrations configured yet. Check back later or refresh to load available integrations.
      </p>
      <button
        onClick={handleRefresh}
        disabled={loading}
        className="mt-4 px-6 py-2 bg-[#00899E] text-white rounded-lg hover:bg-[#006F80] transition-colors disabled:opacity-50"
      >
        {loading ? 'Loading...' : 'Refresh'}
      </button>
    </div>
  );

  return (
    <div className='p-2'>
      {/* Tabs */}
      <div className='flex gap-6 mb-6'>
        {[
          'Integrations',
          'Security',
          'Billing',
          'Notification',
          'Privacy & Data',
          'API Credentials',
        ].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-2 text-[14px] ${tab === activeTab
              ? 'border-b-2 border-[#00899E] font-semibold text-[#00899E]'
              : 'text-gray-500'
              }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Conditional content */}
      {activeTab === 'Security' ? (
        <SecurityLogin />
      ) : activeTab === 'Billing' ? (
        <YourPlan />
      ) : activeTab === 'Privacy & Data' ? (
        <PrivacyData />
      ) : activeTab === 'Notification' ? (
        <Notifications />
      ) : activeTab === 'API Credentials' ? (
        <>
          <h2 className='text-[24px] font-bold mb-2 text-[#3F5265]'>
            Manage API Credentials
          </h2>
          <p className='text-[#657584] text-[16px] mb-6'>
            These credentials are required to authenticate with the API. The
            Track API and App API require separate API keys.
          </p>

          <ApiCredentialsTabs />
        </>
      ) : activeTab === 'Integrations' ? (
        <>
          <div className='flex justify-between items-center mb-2'>
            <div>
              <h2 className='text-[24px] font-bold text-[#3F5265]'>
                Integrations
              </h2>
              <p className='text-[#657584] text-[16px]'>
                Power up your workflow with smart integrations
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className='flex items-center gap-2 px-4 py-2 bg-[#00899E] text-white rounded hover:bg-[#006F80] transition-colors disabled:opacity-50'
            >
              <svg
                className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          <div className='bg-white rounded-md mt-6 border border-gray-200 shadow-sm'>
            {integrations.length === 0 && !loading ? (
              <EmptyState />
            ) : (
              <div className='flex gap-6 p-6'>
                {/* Left Sidebar */}
                <div className='w-1/6'>
                  <h3 className='font-semibold mb-1 text-[#3F5265] text-[18px]'>
                    Category
                  </h3>
                  <p className='text-[14px] text-[#3B82F6] mb-4 font-semibold'>
                    Showing {filteredIntegrations.length} Results
                  </p>

                  <div className='space-y-2 mb-3 p-3 text-[#657584] text-[14px]'>
                    <label className='flex items-center gap-2 cursor-pointer'>
                      <input
                        type='radio'
                        checked={filterMode === 'All'}
                        onChange={() => setFilterMode('All')}
                      />
                      All
                    </label>
                    <label className='flex items-center gap-2 cursor-pointer pt-3'>
                      <input
                        type='radio'
                        checked={filterMode === 'Integrated'}
                        onChange={() => setFilterMode('Integrated')}
                      />
                      Integrated
                    </label>
                  </div>

                  <hr className='my-3' />

                  <ul className='space-y-2 p-3 pt-0'>
                    {categories.map((cat) => (
                      <li key={cat} className='pt-3 text-[#657584] text-[14px]'>
                        <label className='flex items-center gap-2 cursor-pointer'>
                          <input
                            type='checkbox'
                            checked={selectedCategories.includes(cat)}
                            onChange={() => toggleCategory(cat)}
                          />
                          {cat}
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Integrations Grid */}
                <div className='flex-1'>
                  <div className='mb-4'>
                    <input
                      type='text'
                      placeholder='Search for an integration...'
                      className='w-full border border-[#E1EAF0] rounded p-2 bg-[#F5F8FA] text-[14px]'
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>

                  {loading ? (
                    <div className='flex justify-center items-center py-12'>
                      <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#00899E]'></div>
                    </div>
                  ) : (
                    <div className='grid grid-cols-3 gap-4'>
                      {filteredIntegrations.map((item) => (
                        <div
                          key={item.id}
                          onClick={() =>
                            navigate(`/settings/integrations/${item.id}`)
                          }
                          className='border rounded p-8 bg-white shadow-sm flex flex-col items-center text-center relative hover:shadow-lg cursor-pointer group'
                        >
                          <img
                            src={item.logo}
                            alt={item.name}
                            className='h-12 mb-5'
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/48x48?text=Logo';
                            }}
                          />
                          <h4 className='font-semibold text-[20px] text-[#3F5265]'>
                            {item.name}
                            <span className='absolute bottom-6 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 transition-all rounded bg-gray-800 px-2 py-1 text-xs text-white whitespace-nowrap z-10'>
                              View Details
                            </span>
                          </h4>
                          <p className='text-[14px] text-[#3F5265]'>
                            {item.description}
                          </p>
                          {item.integrated && (
                            <span className='mt-2 font-semibold text-[12px] bg-[#ECFDF5] text-[#07A26F] px-2 py-1 absolute ml-[10px] rounded-[45px] left-0 top-0'>
                              Integrated
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {!loading && filteredIntegrations.length === 0 && integrations.length > 0 && (
                    <div className='text-center py-12 text-gray-500'>
                      <p className='text-lg'>No integrations match your filters</p>
                      <p className='text-sm mt-2'>Try adjusting your filters or search</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className='text-center mt-20 text-gray-500 text-lg font-semibold'>
          Work Pending
        </div>
      )}
      <ToastContainer position='top-right' autoClose={2000} />
    </div>
  );
};

export default Settings;